import type { ReactNode } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CompareOutlinedIcon from "@mui/icons-material/CompareOutlined";
import AddIcon from "@mui/icons-material/Add";
import { Box, Button, Drawer, IconButton, Stack, Typography, styled } from "~/common/mui";
import { theme } from "~/app/theme";
import ProductComparisonTitleCard from "./ProductComparisonTitleCard";
import { useComparisonDialog } from "../hooks/useComparisonDialog";

export type ComparisonProduct = {
  code: string;
};

type DrawerProps = {
  open: boolean;
  products: ComparisonProduct[];
  maxSize: number;
  isMobile?: boolean;
  compareLabel: string;
  addProductLabel: string;
  addButtonLabel: string;
  onClose: () => void;
  onOpenQuickEntry: () => void;
  renderProductCard?: (product: ComparisonProduct) => ReactNode;
};

export const ProductComparisonDrawer = ({
  open,
  products,
  maxSize,
  isMobile = false,
  compareLabel,
  addProductLabel,
  onClose,
  onOpenQuickEntry,
  renderProductCard,
}: DrawerProps) => {
  const placeholders = Array.from({ length: maxSize - products.length }, (_, idx) => idx);
  const { openComparisonDialog } = useComparisonDialog();

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      hideBackdrop
      disableScrollLock
      disableEnforceFocus
      disableAutoFocus
      disableRestoreFocus
      transitionDuration={{
        enter: 400,
        exit: 300,
      }}
      ModalProps={{
        slotProps: {
          root: {
            style: {
              pointerEvents: "none",
            },
          },
        },
      }}
      PaperProps={{
        sx: {
          pointerEvents: "auto",
          border: `1px solid ${theme.palette.divider}`,
          borderBottom: "0",
          boxShadow: `${theme.shadows[4]}`,
          background: `${theme.palette.surface.main} !important`,
          borderRadius: "16px 16px 0 0",
          width: "100%",
          maxWidth: 1240,
          margin: "0 auto",
          p: 2,
        },
      }}
    >
      <Stack spacing={1}>
        <PCContainerHeader>
          <Typography component="b" variant="titleLarge" color="text.primary">
            {compareLabel} ({products.length})
          </Typography>

          <Stack direction="row" spacing={1}>
            {isMobile ? (
              <IconButton variant="contained" color="primary" onClick={openComparisonDialog}>
                <CompareOutlinedIcon />
              </IconButton>
            ) : (
              <Button onClick={openComparisonDialog} variant="contained">
                {compareLabel}
              </Button>
            )}

            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </PCContainerHeader>

        <CardsContainer>
          {products.map((product) =>
            renderProductCard ? (
              renderProductCard(product)
            ) : (
              <ProductComparisonTitleCard key={product.code} productCode={product.code} />
            ),
          )}

          {placeholders.map((_, index) =>
            index === 0 ? (
              <AddCard key={index} onClick={onOpenQuickEntry}>
                <Stack gap={1} flexDirection="row" alignItems="center" justifyContent="center">
                  <IconButton variant="contained" size="small" color="secondary">
                    <AddIcon />
                  </IconButton>

                  <Typography variant="bodyLarge" color="textSecondary">
                    {addProductLabel}
                  </Typography>
                </Stack>
              </AddCard>
            ) : (
              <EmptyCard key={index} />
            ),
          )}
        </CardsContainer>
      </Stack>
    </Drawer>
  );
};

const PCContainerHeader = styled(Stack)(() => ({
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "16px",
}));

const CardsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  overflowX: "auto",
  overflowY: "hidden",
}));

const cardStyles = {
  minWidth: 228,
  width: 228,
  height: 76,
};

const EmptyCard = styled(Box)(({ theme }) => ({
  ...cardStyles,
  border: `1px dashed ${theme.palette.divider}`,
  backgroundColor: theme.palette.primary.contrastText,
  borderRadius: theme.spacing(1.5),
}));

const AddCard = styled(Stack)(({ theme }) => ({
  ...cardStyles,
  backgroundColor: theme.palette.primary.contrastText,
  padding: `${theme.spacing(2)} ${theme.spacing(2.5)}`,
  border: `1px dashed ${theme.palette.divider}`,
  borderRadius: theme.spacing(1.5),
  alignItems: "center",
  justifyContent: "center",

  "&:hover": {
    cursor: "pointer",
  },
}));
